import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

type UserRef = {
  id: number;
  email?: string;
};

type ApprovalStatusRef = {
  id: number;
  title?: string;
};

type EscalationTypeRef = {
  id: number;
  name?: string;
};

type AssignmentTypeRef = {
  id: number;
  name?: string;
  level?: number;
};

type ProductRef = {
  id: number;
  name?: string;
};

type OpportunityRef = {
  id: string;
  customer_id?: string | null;
};

type ApprovalRequestItemRef = {
  id: number;
  customer_name?: string | null;
  opportunity_name?: string | null;
  opportunity_product_name?: string | null;
  pov_name?: string | null;
};

type ApprovalRequestRef = {
  id: number;
  title?: string | null;
  requested_item_type?: string | null;
  justification?: string | null;
  approver_note?: string | null;
  status?: ApprovalStatusRef | null;
  status_name?: string | null;
  requestor?: UserRef | null;
  approver?: UserRef | null;
  requested_item?: ApprovalRequestItemRef | null;
};

type AssignmentRef = {
  id: number;
  assignment_type?: string | null;
  status?: string | null;
  assigned_to?: UserRef | null;
  team_name?: string | null;
  created?: string | null;
};

type EscalationRef = {
  id?: number;
  opportunity?: OpportunityRef | null;
  product?: ProductRef | null;
  escalation_type?: EscalationTypeRef | null;
  status?: ApprovalStatusRef | null;
  requested_by?: UserRef | null;
  priority?: string | null;
  stage?: number | null;
  description?: string | null;
};

export type EscalationApprovalRecord = {
  id?: number;
  escalation?: EscalationRef | null;
  approval_request?: ApprovalRequestRef | null;
  level?: number | null;
  target_assignment_type?: AssignmentTypeRef | null;
  assignment?: AssignmentRef | null;
  created?: string;
  last_updated?: string;
};

interface Props {
  open: boolean;
  editItem: EscalationApprovalRecord | null;
  onClose: () => void;
  readOnly?: boolean;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const EscalationApprovalViewDialog: React.FC<Props> = ({
                                                         open,
                                                         editItem,
                                                         onClose,
                                                       }) => {
  if (!editItem) return null;

  const escalation = editItem.escalation ?? null;
  const approvalRequest = editItem.approval_request;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Escalation Approval Details</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          <Typography variant="body2">
            <strong>Workflow Approval ID:</strong> {editItem.id ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Escalation ID:</strong> {editItem.escalation?.id ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Approval Request ID:</strong> {approvalRequest?.id ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Level:</strong> {editItem.level ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Target Assignment Type:</strong> {editItem.target_assignment_type?.name ?? ''}
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong> {formatDateTime(editItem.created)}
          </Typography>
          <Typography variant="body2">
            <strong>Last Updated:</strong> {formatDateTime(editItem.last_updated)}
          </Typography>

          {approvalRequest && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Generic Approval Request</Typography>

              <Typography variant="body2">
                <strong>Title:</strong> {approvalRequest.title ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Requested Item Type:</strong> {approvalRequest.requested_item_type ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {approvalRequest.status?.title ?? approvalRequest.status_name ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Requestor:</strong> {approvalRequest.requestor?.email ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Approver:</strong> {approvalRequest.approver?.email ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Justification:</strong>
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {approvalRequest.justification ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Approver Note:</strong>
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {approvalRequest.approver_note ?? ''}
              </Typography>
            </>
          )}

          {escalation && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Escalation</Typography>

              <Typography variant="body2">
                <strong>Product:</strong> {escalation.product?.name ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Opportunity:</strong> {escalation.opportunity?.id ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Escalation Type:</strong> {escalation.escalation_type?.name ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {escalation.status?.title ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Requested By:</strong> {escalation.requested_by?.email ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Priority:</strong> {escalation.priority ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Stage:</strong> {escalation.stage ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {escalation.description ?? ''}
              </Typography>
            </>
          )}

          {editItem.assignment && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">Linked Assignment</Typography>

              <Typography variant="body2">
                <strong>Assignment ID:</strong> {editItem.assignment.id}
              </Typography>
              <Typography variant="body2">
                <strong>Assignment Type:</strong> {editItem.assignment.assignment_type ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {editItem.assignment.status ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Assigned To:</strong> {editItem.assignment.assigned_to?.email ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Team Name:</strong> {editItem.assignment.team_name ?? ''}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDateTime(editItem.assignment.created)}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EscalationApprovalViewDialog;